import React, { useState, useMemo, useEffect } from 'react';
import { useMandantStore } from '../../store/mandantStore';
import { loadSetting, saveSetting } from '../../services/settingsService';
import { isGmailConnected, sendEmailViaGmail } from '../../services/gmailService';

const EMAIL_KEY = 'emails_v1';

// ── Typen ─────────────────────────────────────────────────────────────────

type EmailStatus = 'entwurf' | 'gesendet' | 'fehler';
type EmailVorlageTyp =
  | 'Terminbestätigung'
  | 'Erinnerung VGÜ fällig'
  | 'Befund übermitteln'
  | 'ASA-Einladung'
  | 'Zahlungserinnerung'
  | 'Sonstiges';

interface EmailVorlage {
  typ:     EmailVorlageTyp;
  betreff: string;
  text:    string;
}

interface Email {
  id:          string;
  datum:       string;
  an:          string;
  betreff:     string;
  vorschau:    string;
  status:      EmailStatus;
  mandantId:   string;
  vorlageTyp?: EmailVorlageTyp;
}

// ── Vorlagen ──────────────────────────────────────────────────────────────

const VORLAGEN: EmailVorlage[] = [
  {
    typ: 'Terminbestätigung',
    betreff: 'Terminbestätigung Arbeitsmedizinische Untersuchung',
    text: `Sehr geehrte Damen und Herren,

ich bestätige den vereinbarten Termin für die arbeitsmedizinische Untersuchung:

  Datum:    [DATUM]
  Uhrzeit:  [UHRZEIT]
  Ort:      [ORT]

Bitte bringen Sie folgende Unterlagen mit:
- Dienstzettel / Stellenbeschreibung
- Bisherige Vorsorgekartei (falls vorhanden)
- ggf. Röntgenbefunde / Laborbefunde

Bei Fragen stehe ich gerne zur Verfügung.

Mit freundlichen Grüßen
Dr. med. Klaus Karrer, MSc.
Arbeitsmediziner`,
  },
  {
    typ: 'Erinnerung VGÜ fällig',
    betreff: 'Erinnerung: Arbeitsmedizinische Vorsorgeuntersuchung fällig',
    text: `Sehr geehrte Damen und Herren,

ich möchte Sie darauf hinweisen, dass die nächste Vorsorgeuntersuchung fällig ist:

  Mitarbeiter/in: [NAME]
  Tatbestand:     [TATBESTAND]
  Fällig seit:    [DATUM]

Bitte vereinbaren Sie zeitnah einen Termin unter +43 664 XXXXXXX
oder per E-Mail an mail@dr-karrer.com.

Mit freundlichen Grüßen
Dr. med. Klaus Karrer, MSc.
Arbeitsmediziner`,
  },
  {
    typ: 'Befund übermitteln',
    betreff: 'Übermittlung arbeitsmedizinischer Befund',
    text: `Sehr geehrte Damen und Herren,

anbei übermittle ich den arbeitsmedizinischen Befund für:

  Mitarbeiter/in: [NAME]
  Untersuchung:   [ART]
  Datum:          [DATUM]

HINWEIS: Dieser Befund unterliegt dem Datenschutz (DSGVO).
Er ist ausschließlich für die betriebliche Arbeitsmedizin bestimmt
und darf nicht an Dritte weitergegeben werden.

Mit freundlichen Grüßen
Dr. med. Klaus Karrer, MSc.
Arbeitsmediziner`,
  },
  {
    typ: 'ASA-Einladung',
    betreff: 'Einladung ASA-Sitzung',
    text: `Sehr geehrte Damen und Herren,

ich lade Sie herzlich zur nächsten ASA-Sitzung (Arbeitsschutzausschuss) ein:

  Datum:    [DATUM]
  Uhrzeit:  [UHRZEIT]
  Ort:      [ORT]

Vorläufige Tagesordnung:
  1. Auswertung Unfallstatistik
  2. Stand Vorsorgeuntersuchungen
  3. Begehungsergebnisse
  4. Allfälliges

Bitte bestätigen Sie Ihre Teilnahme.

Mit freundlichen Grüßen
Dr. med. Klaus Karrer, MSc.
Arbeitsmediziner`,
  },
  {
    typ: 'Zahlungserinnerung',
    betreff: 'Zahlungserinnerung Rechnung [RECHNUNGSNR]',
    text: `Sehr geehrte Damen und Herren,

bei der Durchsicht meiner Buchhaltung habe ich festgestellt, dass folgende
Rechnung noch offen ist:

  Rechnungsnummer: [RECHNUNGSNR]
  Rechnungsdatum:  [DATUM]
  Betrag:          [BETRAG] €

Ich bitte Sie, den offenen Betrag bis zum [FAELLIGKEITSDATUM] auf folgendes
Konto zu überweisen:

  IBAN: AT__ XXXX XXXX XXXX XXXX
  BIC:  XXXXXXXX
  Verwendungszweck: Rechnung [RECHNUNGSNR]

Bei bereits erfolgter Überweisung bitte ich dieses Schreiben zu ignorieren.

Mit freundlichen Grüßen
Dr. med. Klaus Karrer, MSc.
Arbeitsmediziner`,
  },
  {
    typ: 'Sonstiges',
    betreff: '',
    text: '',
  },
];

// Platzhalter mit aktuellen Werten befüllen
function applyDefaults(text: string, mandantName: string): string {
  const today = new Date().toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return text
    .replace(/\[DATUM\]/g, today)
    .replace(/\[MANDANT\]/g, mandantName);
}

// ── Status-Config ─────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<EmailStatus, { label: string; color: string; dot: string }> = {
  entwurf:  { label: 'Entwurf',  color: 'bg-gray-100 text-gray-700',   dot: 'bg-gray-400' },
  gesendet: { label: 'Gesendet', color: 'bg-green-100 text-green-800', dot: 'bg-green-500' },
  fehler:   { label: 'Fehler',   color: 'bg-red-100 text-red-700',     dot: 'bg-red-500' },
};

// ── Composer ──────────────────────────────────────────────────────────────

function EmailComposer({ onSend }: { onSend: (e: Omit<Email, 'id'>) => void }): React.JSX.Element {
  const mandant    = useMandantStore(s => s.getAktiverMandant());
  const gmailOk    = isGmailConnected();

  const [an,       setAn]       = useState('');
  const [betreff,  setBetreff]  = useState('');
  const [text,     setText]     = useState('');
  const [vorlage,  setVorlage]  = useState<EmailVorlageTyp>('Sonstiges');
  const [busy,     setBusy]     = useState(false);
  const [sent,     setSent]     = useState(false);
  const [err,      setErr]      = useState('');

  const handleVorlage = (typ: EmailVorlageTyp): void => {
    setVorlage(typ);
    const v = VORLAGEN.find(x => x.typ === typ);
    if (v !== undefined) {
      setBetreff(applyDefaults(v.betreff, mandant?.name ?? ''));
      setText(applyDefaults(v.text, mandant?.name ?? ''));
    }
  };

  const handleSend = async (via: 'gmail' | 'mailto' | 'draft'): Promise<void> => {
    if (via !== 'draft' && !an.trim()) return;
    setErr('');
    setBusy(true);
    try {
      if (via === 'gmail') {
        await sendEmailViaGmail({ to: an.trim(), subject: betreff, body: text });
      } else if (via === 'mailto') {
        const url = `mailto:${encodeURIComponent(an.trim())}?subject=${encodeURIComponent(betreff)}&body=${encodeURIComponent(text)}`;
        window.open(url, '_blank');
      }
      onSend({
        datum: new Date().toISOString().slice(0, 10),
        an: an.trim(), betreff, vorschau: text.slice(0, 100),
        status: via === 'draft' ? 'entwurf' : 'gesendet',
        mandantId: mandant?.id ?? '', vorlageTyp: vorlage,
      });
      setSent(true);
      setTimeout(() => {
        setSent(false); setAn(''); setBetreff(''); setText(''); setVorlage('Sonstiges');
      }, 1500);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="card text-center py-10">
        <p className="text-4xl mb-3">✅</p>
        <p className="font-semibold text-green-700 dark:text-green-400">E-Mail gesendet</p>
      </div>
    );
  }

  return (
    <div className="card space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Neue E-Mail</h2>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          gmailOk
            ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400'
            : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
        }`}>
          {gmailOk ? '✓ Gmail verbunden' : 'Gmail nicht verbunden'}
        </span>
      </div>

      {/* Vorlage */}
      <div className="space-y-1">
        <label className="label-text">Vorlage</label>
        <div className="flex flex-wrap gap-1.5">
          {VORLAGEN.map(v => (
            <button key={v.typ} onClick={() => { handleVorlage(v.typ); }}
              className={`text-xs px-3 py-1 rounded-full font-medium transition-colors
                ${vorlage === v.typ
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}>
              {v.typ}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-1">
        <label className="label-text">An</label>
        <input type="email" value={an} onChange={e => { setAn(e.target.value); }}
          placeholder="empfaenger@beispiel.at" className="input-field"
          autoCapitalize="none" autoCorrect="off" spellCheck={false} />
      </div>

      <div className="space-y-1">
        <label className="label-text">Betreff</label>
        <input type="text" value={betreff} onChange={e => { setBetreff(e.target.value); }}
          placeholder="Betreff" className="input-field" />
      </div>

      <div className="space-y-1">
        <label className="label-text">Nachricht</label>
        <textarea value={text} onChange={e => { setText(e.target.value); }}
          rows={12} className="input-field font-mono text-xs resize-y"
          placeholder="E-Mail-Text…" />
        <p className="text-xs text-gray-400">
          Platzhalter: [NAME] [TATBESTAND] [UHRZEIT] [ORT] [RECHNUNGSNR] [BETRAG] [FAELLIGKEITSDATUM]
        </p>
      </div>

      {err !== '' && (
        <p className="text-xs text-red-600 dark:text-red-400 font-medium">❌ {err}</p>
      )}

      <div className="flex gap-2 justify-end pt-1 flex-wrap">
        <button onClick={() => { void handleSend('draft'); }} disabled={busy}
          className="btn-secondary text-sm disabled:opacity-50">
          Entwurf speichern
        </button>
        <button onClick={() => { void handleSend('mailto'); }}
          disabled={busy || !an.trim()}
          className="btn-secondary text-sm disabled:opacity-50">
          {busy ? '…' : '✉ E-Mail-Client'}
        </button>
        {gmailOk && (
          <button onClick={() => { void handleSend('gmail'); }}
            disabled={busy || !an.trim()}
            className="btn-primary text-sm disabled:opacity-50">
            {busy ? '…' : '✉ Via Gmail senden'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Postausgang ────────────────────────────────────────────────────────────

function Postausgang({ emails }: { emails: Email[] }): React.JSX.Element {
  if (emails.length === 0) {
    return (
      <div className="card text-center py-10 text-gray-400">
        <p className="text-3xl mb-2">✉</p>
        <p className="font-medium">Noch keine E-Mails</p>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden p-0">
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {[...emails].reverse().map(e => {
          const sc = STATUS_CONFIG[e.status];
          return (
            <div key={e.id} className="flex items-start gap-3 px-4 py-3">
              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${sc.dot}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {e.betreff}
                  </p>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${sc.color}`}>
                    {sc.label}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5 truncate">
                  An: {e.an} · {e.datum} · {e.mandantId}
                </p>
                <p className="text-xs text-gray-500 mt-0.5 truncate italic">{e.vorschau}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Hauptkomponente ───────────────────────────────────────────────────────

type EmailTab = 'neu' | 'postausgang';

export function EmailModul(): React.JSX.Element {
  const [tab, setTab]       = useState<EmailTab>('neu');
  const [emails, setEmails] = useState<Email[]>([]);

  useEffect(() => {
    void loadSetting<Email[]>(EMAIL_KEY, []).then(setEmails);
  }, []);

  const gesendet = useMemo(() => emails.filter(e => e.status === 'gesendet').length, [emails]);
  const entw     = useMemo(() => emails.filter(e => e.status === 'entwurf').length, [emails]);

  const handleSend = (e: Omit<Email, 'id'>): void => {
    const neu: Email = { ...e, id: crypto.randomUUID() };
    setEmails(prev => {
      const updated = [...prev, neu];
      void saveSetting(EMAIL_KEY, updated);
      return updated;
    });
    if (e.status === 'gesendet') setTab('postausgang');
  };

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 pt-4 flex gap-1">
        {([
          { key: 'neu' as const,         label: 'Neue E-Mail',           icon: '✉' },
          { key: 'postausgang' as const, label: `Gesendet (${gesendet})`, icon: '📤' },
        ]).map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); }}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors
              ${tab === t.key
                ? 'border-blue-600 text-blue-700 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}>
            <span>{t.icon}</span>
            <span>{t.label}</span>
            {t.key === 'postausgang' && entw > 0 && (
              <span className="ml-1 text-xs bg-orange-100 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400 px-1.5 py-0.5 rounded-full">
                {entw} Entw.
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-6 max-w-3xl mx-auto w-full space-y-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">E-Mail</h1>
          <p className="text-sm text-gray-500 mt-0.5">Vorlagen & Postausgang</p>
        </div>
        {tab === 'neu'         && <EmailComposer onSend={handleSend} />}
        {tab === 'postausgang' && <Postausgang emails={emails} />}
      </div>
    </div>
  );
}
